import { createContext, FC, PropsWithChildren, useContext, useEffect, useRef, useState } from "react";

import { ParticipantDetail } from "@asd/customer-portal-api";

import { ParticipantDetailsService } from "~/services";

type DashboardStaffProviderType = {
  participantDetails: ParticipantDetail[] | null;
  error: string | null;
  isLoading: boolean;
  selectedParticipant: ParticipantDetail | null;
  setSelectedParticipant: (_participant: ParticipantDetail | null) => void;
};

const DashboardStaffContext = createContext<DashboardStaffProviderType>({} as DashboardStaffProviderType);

export const useDashboardStaffProvider = () => useContext(DashboardStaffContext);

export const DashboardStaffProvider: FC<PropsWithChildren> = ({ children }) => {
  const [participantDetails, setParticipantDetails] = useState([] as ParticipantDetail[] | null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedParticipant, setSelectedParticipant] = useState<ParticipantDetail | null>(null);

  const isInitialized = useRef(false);
  const dataPromise = useRef<Promise<void> | null>(null);
  const dataAbortController = useRef<AbortController>(new AbortController());

  useEffect(() => {
    if (!isInitialized.current) {
      dataAbortController.current?.abort();
      dataAbortController.current = new AbortController();

      setIsLoading(true);
      setError(null);
      const thisPromise = ParticipantDetailsService.getParticipantDetails({
        signal: dataAbortController.current.signal,
      })
        .then((result) => {
          if (thisPromise === dataPromise.current) {
            if (!result?.data) {
              throw new Error("");
            }

            setError(null);

            setParticipantDetails(result.data);
          }
        })
        .catch(() => {
          setError("Failed to load participant detail data. Please try again later");
        })
        .finally(() => {
          if (thisPromise === dataPromise.current) {
            isInitialized.current = true;
            setIsLoading(false);
          }
        });

      dataPromise.current = thisPromise;
    }

    return () => {
      dataAbortController.current?.abort();
    };
  }, []);

  return (
    <DashboardStaffContext.Provider value={{ participantDetails, error, isLoading, selectedParticipant, setSelectedParticipant }}>
      {children}
    </DashboardStaffContext.Provider>
  );
};
